import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { planningLevel } from '../nav/planningLevel';
import { createPortal } from 'react-dom';
import { ArrowUpRight, SlidersHorizontal, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getPlView, listLines, fillCsForecast, fillTaxForecast } from '../api/financials_pl';
import { useP } from '../i18n/useP';
import PageShell from '../components/PageShell';
import SegmentedToggle from '../components/SegmentedToggle';

import { useHubExcelExport } from '../components/ExcelExportButton';
import { useRegisterPageExports } from '../exports/pageExports';
import PrintReportDocument from './PrintReportDocument';
import { downloadFile } from '../api/client';
import { usePersistentValue } from '../hooks/usePersistentState';
import { useForecastAssumptions } from '../hooks/useForecastAssumptions';
import ProfitLossPage from './ProfitLossPage';
import ForecastBalanceSheetTab from './ForecastBalanceSheetTab';
import PayrollSocialCard from './financials/PayrollSocialCard';
import PurchasesMonthlyCard from './financials/PurchasesMonthlyCard';
import PlanLandingSynthesis from './financials/PlanLandingSynthesis';
import ForecastCashflowTab from './ForecastCashflowTab';
import ForecastCashPlanTab from './ForecastCashPlanTab';
import HypothesesPanel from './financials/HypothesesPanel';
import VatYearPage from './financials/VatYearPage';
import CashMovesPage from './financials/CashMovesPage';
import StartingPointPage from './financials/StartingPointPage';
import './PrevisionnelHub.css';

/* LES DEUX CHARGES QUI SE DÉDUISENT D'UN TAUX : chacune a sa ligne (par rôle)
   et sa route, qui pose le taux ET remplit les mois ouverts. */
/* `pick` répète la règle du SERVEUR, qui accepte la ligne de la section « tax »
   quand le rôle n'a pas été posé : l'impôt s'applique toujours au résultat
   avant impôts. Sans elle, le champ manquerait là où la route fonctionne. */
const ROLE_RATES = [
  { role: 'social_charges', label: 'Charges sociales', fill: fillCsForecast,
    pick: (ls) => ls.find((x) => x.line_role === 'social_charges') || null },
  { role: 'income_tax', label: 'Imposition', fill: fillTaxForecast,
    pick: (ls) => ls.find((x) => x.line_role === 'income_tax')
      || ls.find((x) => x.section === 'tax') || null },
];

/* Prévisionnel Rapide — hub forward simple (module Planification financière).
     • Synthèse          — CR simplifié + KPI, sur 12 mois (réel + prév.)
     • Compte de résultat — forecast P&L complet (toutes sections)
     • Bilan             — bilan prévisionnel piloté (BFR par driver + roulements)
     • Cash flow         — trésorerie méthode INDIRECTE (depuis le résultat net)
   (Le Plan de liquidité — méthode DIRECTE — vit dans la section Trésorerie :
   vue unique depuis le 2026-08-19, l'onglet d'ici la doublonnait.)
   Les hypothèses BFR (DSO/DPO/jours de stock + acomptes) et le dénouement de
   l'ouverture (roulement créances/dettes) sont édités dans le Bilan (source
   unique). Les ex-onglets « Postes ouverts » / « Sélection PO » ont été retirés
   (repris par le roulement du Bilan) — cf. WIP/dette-technique.md.
   L'onglet « Charges sociales » a été déplacé vers People › Planification
   personnel (après Masse salariale) — sa place thématique. */
/* La SYNTHÈSE a quitté la barre le 2026-09-17 : elle a fusionné avec la
   « Synthèse financière » de la Vue de direction, qui disait la même cascade
   sur le réel et porte désormais l'exercice sous un sélecteur de période. Deux
   écrans pour un compte de résultat, c'était un jour où l'on ne sait plus
   lequel fait foi. Le composant `ForecastSummary` reste monté par le PDF du
   Prévisionnel (`ForecastSummaryPrint`). */
/* PLUS D'ONGLETS DANS CETTE SECTION (2026-09-18) : elle n'a qu'une page, le
   compte de résultat, parcouru en ÉTAPES. Les onglets « Revenus & Marges »,
   « Charges de personnel » et « Autres charges » disaient ce que les étapes
   disent, sur les mêmes lignes et avec les mêmes règles ; deux chemins pour une
   même table, dont l'un dirait qu'on planifie et l'autre qu'on lit, ne peuvent
   pas cohabiter. Sont partis avant eux : la Synthèse, fusionnée le 2026-09-17
   avec la « Synthèse financière » de la Vue de direction (le composant
   `ForecastSummary` reste monté par le PDF) ; puis, le 2026-09-18, le BILAN, le
   FLUX DE TRÉSORERIE et le CAPEX vers « Bilan prévisionnel », parce que cette
   section construit le RÉSULTAT et que les deux états en DÉCOULENT ; et avec
   eux les CHARGES SOCIALES, le DÉCOMPTE PERS. et les MOUVEMENTS EXCEPTIONNELS,
   qui sont des décaissements à échéance. */

export default function PrevisionnelHub({ activeTenant, addToast, year, onYearChange, subject = 'wc', scopes = {}, setPage = null, onGoSettings = null }) {
  const { t } = useTranslation();
  const p = useP();
  const [showPrint, setShowPrint] = useState(false);
  const [showAssum, setShowAssum] = useState(false);
  const tid = activeTenant?.id;
  const [unit, setUnit] = usePersistentValue(`prev_unit_${tid}`, 'kchf');
  // Hypothèses BFRE + cash flow persistées en base (tenant × année).
  const { a, setKey, setKeyNow, reload: reloadAssum } = useForecastAssumptions(tid, year);
  /* Ce que les écrans montés ici doivent RELIRE quand une hypothèse change. */
  const [dataKey, setDataKey] = useState(0);
  const assumptions = {
    dso: a.dso, dpo: a.dpo, dio: a.dio, pac: a.pac,
    setDso: (v) => setKey('dso', v), setDpo: (v) => setKey('dpo', v),
    setDio: (v) => setKey('dio', v), setPac: (v) => setKey('pac', v),
  };
  /* QUATRE SUJETS, CHACUN SES ÉTAPES (2026-09-19). La section ne construisait
     que le compte de résultat ; elle construit désormais TOUT le prévisionnel,
     et les postes de bilan qui l'accompagnent ne sont plus rangés ailleurs.

     Le SUJET dit ce qu'on construit, l'ÉTAPE où l'on en est dedans. ⚠️ Le sujet
     passe DEVANT l'étape, contre le standard qui le veut en dernier : ici il
     COMMANDE la liste des étapes, c'est une hiérarchie et non une préférence
     d'affichage. Ne pas le replacer à droite sans défaire cette dépendance.

     L'ordre des sujets est celui de la SAISIE, pas celui du calcul : les
     intérêts du sujet 1 dépendent du besoin de trésorerie que le sujet 3
     révèle. Une seconde passe reste nécessaire, et l'écran ne prétend pas
     l'inverse.

     ⚠️ Les écrans montés ici sont les MÊMES composants que ceux du Bilan
     prévisionnel : un seul moteur, deux entrées. Ne pas en recopier un. */
  const SUBJECTS = [
    /* TROIS SUJETS, CHACUN UN CYCLE COMPLET (2026-09-19) : ce qui ENTRE, ce qui
       SORT, et ce qui FINANCE l'écart. Chaque sujet porte la charge ou le
       revenu ET son mouvement de trésorerie, si bien qu'on ne planifie plus une
       ligne sans voir quand elle se paie.

       Les cinq sujets d'avant séparaient le compte de résultat (Activité) de
       ses encaissements (Encaissements et paiements) : on posait un revenu sans
       sa créance, une charge sans sa dette, et il fallait revenir. Le sujet
       « Investissements » est parti avec eux, il a désormais SA SECTION, le
       Plan d'investissement ; « EBIT » aussi, la Synthèse lisant le compte de
       résultat entier juste après. */
    /* LES SECTIONS SUIVENT LES CYCLES, PLUS LES NATURES (décision d'Olivier,
       2026-09-25). « Revenus et encaissements » face à « Charges et paiements »
       rangeait par nature comptable, ce qui coupait un même poste en deux : le
       revenu d'un côté, la créance qu'il produit de l'autre. Les sections
       disent désormais un CYCLE, comme le tableau de flux : ce que le bilan
       porte et qui se dénoue, ce qu'on exploite, ce qu'on investit (sa propre
       section), et ce qui finance le reste.

       FONDS DE ROULEMENT, et non « soldes » : ces écrans ne portent pas un
       montant à une date, ils portent un poste ENTIER, son ouverture et les
       flux qui l'alimentent. On y pose des règles qu'on ne retouche plus
       (délais, profils d'encaissement), et l'on n'y revient qu'à la marge :
       mettre à jour un prévisionnel se joue dès lors dans « Opérations ». */
    { id: 'wc', label: p('Plan de liquidité'), plain: true, steps: [
      /* LE PLAN DE LIQUIDITÉ OUVRE (2026-09-25) : il montre ce que tous les
         postes du dessous produisent, et l'on y revient après chaque réglage.
         Il se LIT, il ne porte donc pas de rang. */
      { id: 'liquidity', label: p('Plan de liquidité'), read: true },
      /* LES EMPRUNTS OUVRENT LES DÉTAILS (demande d'Olivier, 2026-09-26) : on
         regarde d'abord de quoi on a besoin pour tenir, avant de régler les
         postes qui creusent le trou. */
      /* LES EMPRUNTS ET APPORTS SONT UNE SECTION DE FINANCE (2026-09-27) : les
         contrats se saisissent là-bas, le plan les lit. */
      /* LE POINT DE DÉPART OUVRE LES DÉTAILS (demande d'Olivier, 2026-09-28) :
         la trésorerie de départ et les postes ouverts, ce dont le plan part. */
      { id: 'start', label: p('Point de départ') },
      /* CRÉANCES CLIENTS, FOURNISSEURS, ACOMPTES ET TVA sont partis à la
         section Fonds de roulement (demande d'Olivier, 2026-09-30) : deux
         chemins vers les mêmes écrans. Leurs cas (`wc/clients`…) restent
         routés le temps que les sessions ouvertes sur eux se referment. */
      /* UN SEUL DÉTAIL POUR LES CHARGES SOCIALES (2026-09-26) : « Décaissements
         charges sociales » disait la même chose que ce flux, le couru puis le
         payé puis le solde. La CHARGE se pose au Plan opérationnel, avec le
         personnel qui la produit ; ici ne reste que sa sortie de trésorerie.
         Le flux vivait dans un groupe « Flux » du rang des vues, comme si
         c'était une autre représentation du plan : c'en est le DÉTAIL. */
      { id: 'socialflux', label: p('Flux charges sociales') },
      /* LES MOUVEMENTS EXCEPTIONNELS VIVENT DANS « OPÉRATIONS SPÉCIALES » (demande d'Olivier, 2026-10-22) : plus de raccourci ici. */
    ] },
    /* OPÉRATIONS : ce que l'entreprise gagne et dépense en exploitant. Les
       revenus rejoignent les charges qu'ils portent, deux sections pour un
       seul compte d'exploitation étant un aller-retour à chaque lecture. */
    /* Le titre suit la SECTION du menu (« Résultat prévisionnel », 2026-09-27). */
    { id: 'out', label: t('nav:items.forecast-out'), views: true, steps: [
      /* PAS D'ÉTAPE « REVENUS » (2026-09-25) : « Coûts directs » montre DÉJÀ
         les revenus au-dessus des coûts qu'ils portent, une marge ne se lisant
         pas sans son chiffre d'affaires. Deux étapes pour la même table était
         un écran de plus à ouvrir pour la même chose. */
      /* IMPACTS FINANCIERS A FUSIONNÉ ICI (demande d'Olivier, 2026-09-27) :
         Revenus & Marges ouvre la section, dans l'ordre du compte de résultat,
         et les Débiteurs la ferment, la créance suivant le revenu qui la
         produit. Les deux écrans sont ceux de l'ancienne section, montés tels
         quels. */
      /* LA SYNTHÈSE OUVRE LE PLAN FINANCIER (demande d'Olivier, 2026-09-27) :
         l'atterrissage de l'exercice, compte de résultat puis ratios et
         productivité. */
      { id: 'synthesis', label: p('Synthèse') },
      /* LE COMPTE DE RÉSULTAT ENTIER, À PLANIFIER (demande d'Olivier,
         2026-09-29) : toute la cascade d'un coup, avec les modes de chaque
         ligne ; les vues suivantes en détaillent un bloc. */
      { id: 'result', label: p('Compte de résultat') },
      { id: 'margins', label: p('Revenus & Marges') },
      { id: 'personnel', label: p('Personnel') },
      /* L'ONGLET « CHARGES SOCIALES » EST PARTI (demande d'Olivier,
         2026-10-03) : leur détail se lit sous le Personnel, la carte de
         Salaires prévisionnels, calculée sur la même table. */
      { id: 'other', label: p('Autres charges') },
      /* LES DÉBITEURS sont passés au Plan de liquidité le même jour (demande
         d'Olivier, 2026-09-27) : ce sont des encaissements, pas du résultat. */
    ] },
    /* LE PLAN DE LIQUIDITÉ OUVRE (2026-09-19, soir) : c'est LUI qui montre le
       trou, et l'on ne décide pas d'un emprunt avant de l'avoir vu. Il se LIT,
       il ne porte donc pas de rang. Les mouvements exceptionnels sont partis au
       fonds de roulement le 2026-09-25 : un apport en capital relève du
       financement, mais une correction de débiteur ou une sortie de dépôt sont
       des postes de bilan qu'on dénoue. */
    /* SYNTHÈSE, SANS LE FINANCEMENT (2026-09-25) : les emprunts sont partis au
       fonds de roulement avec le plan qui montre leur besoin. Ne restent que
       les trois états qui RÉSUMENT ce que les sections du dessus ont produit. */
    { id: 'synth', label: p('Synthèse'), views: true, steps: [
      { id: 'result', label: p('Compte de résultat') },
      { id: 'balance', label: p('Bilan'), read: true },
      { id: 'cashflow', label: p('Flux de trésorerie'), read: true },
    ] },
  ];
  /* LE SUJET EST LA SECTION (2026-09-19) : il se choisissait dans une liste
     posée devant les étapes, donc deux gestes pour arriver sur un écran, et la
     liste redisait ce que le menu aurait dit. Chacun des trois sujets est
     désormais une SECTION du menu, et la page n'en porte plus que les étapes.
     La clé de page dit lequel on ouvre. */
  const subj = SUBJECTS.find((x) => x.id === subject) || SUBJECTS[0];
  /* CHAQUE SUJET RETIENT SA PROPRE ÉTAPE : revenir à l'Activité ne doit pas
     ramener à l'étape 1 parce qu'on est passé par la Synthèse entre-temps. */
  const [stepBySubj, setStepBySubj] = usePersistentValue(`prev_steps_${tid}`, {});
  /* LA REPRÉSENTATION DU PLAN MONTE SUR LA RANGÉE (demande d'Olivier,
     2026-09-25) : table ou graphique d'abord, la période ensuite (elle arrive
     du plan lui-même par le slot), puis le flux qu'on lit. Ces pilules
     vivaient dans l'entête de la carte depuis le 18.09 ; la page porte
     désormais une rangée de vues, elles y sont à leur place. */
  const [planPane, setPlanPane] = usePersistentValue(`prev_pane_${tid}`, 'plan');
  /* Avec People, les charges sociales viennent de Salaires prévisionnels : le
     taux de charges sociales quitte les Hypothèses (2026-10-03). */
  const hasPeople = (activeTenant?.enabled_modules || []).includes('rh');
  /* LE NIVEAU DU CLIENT (décision Olivier, 2026-09-29) : choisi une fois dans
     Paramètres › Planification ; Pro, tout se lit et ses calculs s'ouvrent ; Manuel,
     la table se tape. Plus de bascule sur la page. */
  const planMode = planningLevel(activeTenant) === 'pro' ? 'pro' : 'simple';
  const simplePlan = !!subj.plain && planMode === 'simple';
  const step = !simplePlan && subj.steps.some((st) => st.id === stepBySubj[subj.id])
    ? stepBySubj[subj.id] : subj.steps[0].id;
  const setStep = (v) => setStepBySubj({ ...stepBySubj, [subj.id]: v });
  const here = `${subj.id}/${step}`;

  /* LES ÉTAPES SEULES (2026-09-19) : le rang du sujet est parti au menu, et
     la rangée retrouve le costume commun, libellé au-dessus de sa commande. */
  /* LE RANG NE COMPTE QUE CE QUI SE SAISIT : une étape en lecture (le plan de
     liquidité, le bilan, le flux) n'est pas une marche du parcours, elle n'a
     donc pas de numéro — et elle n'en consomme pas un au passage, sans quoi la
     première étape à saisir s'annoncerait « 2 ». */
  let rank = 0;
  const stepOptions = subj.steps.map((st) => {
    if (!st.read && !subj.plain) rank += 1;
    return { value: st.id, label: st.read ? st.label : `${rank} · ${st.label}` };
  });
  /* UN DÉTAIL DE CALCUL N'EST PAS UNE VUE DE PAGE (2026-09-25) : les huit
     postes ont vécu deux jours en bascule segmentée, alignés avec « Plan de
     liquidité » comme s'ils étaient neuf lectures de même rang. Ils ne le sont
     pas : la page EST le plan, et chacun de ces écrans OUVRE le calcul d'une
     de ses lignes. Ils prennent donc un costume à eux, un groupe de boutons à
     filet annoncé « Modifier les calculs », et l'écran ouvert porte de quoi le
     refermer pour revenir au plan. */
  const plan = subj.plain ? subj.steps[0] : null;
  const detail = subj.plain && step !== plan.id ? subj.steps.find((st) => st.id === step) : null;
  /* LE BOUTON DE FERMETURE VIT DANS L'ENTÊTE DE LA CARTE (proto, 2026-09-25) :
     il a d'abord été posé dans une barre au-dessus, à l'autre bout de l'écran,
     où il se perdait. Il descend donc dans la carte du calcul ouvert, en
     ACTION d'entête (zone 3 du standard), et chaque écran de détail le reçoit
     par `closeAction`. */
  const closeAction = !detail ? null : (
    <button type="button" className="prev-und__x" onClick={() => setStep(plan.id)}>
      {p('Fermer le détail')}<X size={14} aria-hidden="true" />
    </button>
  );
  /* La place d'accueil n'existe qu'une fois la rangée rendue : on la prend
     après le montage, comme le plan de liquidité prend la sienne. */
  const [connSlot, setConnSlot] = useState(null);
  useLayoutEffect(() => { setConnSlot(document.getElementById('lk-views-right')); }, [subject]);
  /* LA LIGNE DE CRÉDIT (demande d'Olivier, 2026-09-30) : elle se règle dans
     Paramètres › Finance ; le plan de liquidité y mène, « Fermer et revenir »
     ramène au plan. */
  const creditBtn = subj.plain && onGoSettings ? (
    <button type="button" className="prev-und__b" onClick={() => onGoSettings('finance')}>
      {p('Ligne de crédit')}<ArrowUpRight size={13} aria-hidden="true" />
    </button>
  ) : null;
  const plViewRow = simplePlan ? (
    /* En Simple, rien à ouvrir que les frais de personnel, qui se saisissent
       dans leur section. */
    <div className="prev-nav prev-nav--plain">
      <div className="prev-nav__r prev-nav__r--stack">
        <span className="prev-nav__l">{p('Modifier les calculs')}</span>
        <div className="prev-und" role="group" aria-label={p('Modifier les calculs')}>
          <button type="button" className="prev-und__b" onClick={() => setPage && setPage('fin-payroll')}>
            {p('Salaires prévisionnels')}<ArrowUpRight size={13} aria-hidden="true" />
          </button>
          {creditBtn}
        </div>
      </div>
    </div>
  ) : subj.plain ? (
    <div className="prev-nav prev-nav--plain">
      <div className="prev-nav__r prev-nav__r--stack">
        <span className="prev-nav__l">{p('Modifier les calculs')}</span>
        <div className="prev-und" role="group" aria-label={p('Modifier les calculs')}>
          {subj.steps.filter((st) => st.id !== plan.id).map((st) => (
            <button key={st.id} type="button"
                    className={`prev-und__b${step === st.id ? ' is-on' : ''}`}
                    aria-pressed={step === st.id}
                    onClick={() => setStep(step === st.id ? plan.id : st.id)}>
              {st.label}<ArrowUpRight size={13} aria-hidden="true" />
            </button>
          ))}
          {creditBtn}
        </div>
      </div>
    </div>
  ) : subj.views ? null : (
    <div className="prev-nav">
      <div className="prev-nav__r prev-nav__r--stack">
        <span className="prev-nav__l">{p('Étapes')}</span>
        <SegmentedToggle size="lg" value={step} onChange={setStep}
                         ariaLabel={p('Étape')} options={stepOptions} />
      </div>
    </div>
  );

  /* LES DEUX TAUX se règlent à côté des connexions (2026-09-18) : les charges
     sociales et l'impôt sont les deux charges que cet écran ne tape pas, il les
     déduit d'un taux. Ils se réglaient au fond de la table, sur leur ligne, là
     où personne n'allait les chercher. */
  const [rateLines, setRateLines] = useState({});
  const [rates, setRates] = useState({});
  const [rateDone, setRateDone] = useState(0);
  useEffect(() => {
    if (!tid) return undefined;
    let alive = true;
    listLines(tid).then((r) => {
      const ls = (r && r.data) || r || [];
      if (!alive) return;
      const byRole = {};
      const shown = {};
      ROLE_RATES.forEach(({ role, pick }) => {
        const l = pick(ls);
        byRole[role] = l;
        shown[role] = l && l.forecast_rate != null ? String(Math.round(l.forecast_rate * 1000) / 10) : '';
      });
      setRateLines(byRole);
      setRates(shown);
    }).catch(() => { if (alive) setRateLines({}); });
    return () => { alive = false; };
  }, [tid, year, rateDone]);
  /* Le taux passe par la ROUTE dédiée de sa ligne : c'est le serveur qui pose
     le taux ET remplit les mois ouverts. Écrire la ligne à la main laisserait
     la règle sans ses montants. */
  const applyRate = async (role, raw) => {
    const def = ROLE_RATES.find((r) => r.role === role);
    const v = parseFloat(String(raw).replace(',', '.'));
    if (!def || !rateLines[role] || !Number.isFinite(v) || v <= 0 || v > 100) return;
    const res = await def.fill(tid, { year, taux: v / 100 }).catch(() => null);
    if (res && res.error && addToast) addToast(res.error, 'error');
    setRateDone((n) => n + 1);
    setDataKey((n) => n + 1);
  };
  const rateInput = (role) => (
    <span className="prev-rate__row">
      <input type="text" inputMode="decimal" className="prev-rate__in" value={rates[role] || ''}
             aria-label={p(ROLE_RATES.find((r) => r.role === role).label)}
             onChange={(e) => setRates((r) => ({ ...r, [role]: e.target.value.replace(/[^0-9.,]/g, '') }))}
             onBlur={(e) => applyRate(role, e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
      <span className="prev-rate__u">%</span>
    </span>
  );

  /* LE TAUX CONSTATÉ SUR LE RÉEL, en repère sous le taux qu'on pose : on
     n'invente pas un taux de charges sociales, on part de celui que l'exercice
     a produit. Il vit dans la vue du compte de résultat, que la fenêtre ne
     monte pas ; on ne le charge donc que quand elle s'ouvre. */
  const [csReal, setCsReal] = useState(null);
  useEffect(() => {
    if (!tid || !showAssum) return undefined;
    let alive = true;
    getPlView(tid, year)
      .then((r) => { if (alive) setCsReal(r && r.data ? r.data.taux_cs_constate : null); })
      .catch(() => { if (alive) setCsReal(null); });
    return () => { alive = false; };
  }, [tid, year, showAssum]);

  /* LE TAUX D'IMPOSITION REVIENT DANS LA FENÊTRE (décision d'Olivier,
     2026-09-24). Il a vécu cinq jours en bandeau au-dessus de la cascade de la
     Synthèse, au motif que l'impôt se calcule après les intérêts : mais c'est
     un RÉGLAGE, posé une fois pour l'exercice, et les quatre réglages qui
     commandent l'écran se tiennent à un seul endroit. Le bandeau, lui, ne se
     voyait que d'une étape sur douze. */
  /* LE VOLET DES HYPOTHÈSES remplace la fenêtre (décision Olivier,
     2026-09-27) : il pousse la table au lieu de la recouvrir. */
  const assumPanel = showAssum && (
    <HypothesesPanel activeTenant={activeTenant} year={year} unit={unit}
                     a={a} setKeyNow={setKeyNow} reloadKey={dataKey}
                     onChanged={() => setDataKey((n) => n + 1)}
                     rateSocial={hasPeople ? null
                       : (rateLines.social_charges ? rateInput('social_charges') : <span className="prev-assum__na">{p('aucune ligne')}</span>)}
                     rateTax={rateLines.income_tax ? rateInput('income_tax') : <span className="prev-assum__na">{p('aucune ligne')}</span>}
                     csReal={csReal} onClose={() => setShowAssum(false)} />
  );

  /* UN BOUTON, PAS UNE ÉTAPE (2026-09-19, soir) : les quatre réglages qui
     commandent tout l'écran vivaient à deux endroits, trois dans une étape
     numérotée du sujet « Charges », la connexion des ventes sur la rangée. Ils
     tiennent désormais dans UNE fenêtre, ouverte de n'importe quelle section :
     on les pose une fois, on n'y revient qu'en cas d'essai, et la connexion
     des ventes reste à un clic du chiffre qu'elle produit. Une fenêtre pour un
     RÉGLAGE reste licite ; c'est la fenêtre de NAVIGATION qui est proscrite. */
  /* LA CONNEXION SE LIT SUR LA RANGÉE (décision d'Olivier, 2026-09-24), à
     gauche du bouton qui la règle. Elle s'annonçait jusque-là dans les cartes
     « Source » de chaque écran de trésorerie, donc autant de fois qu'il y avait
     d'onglets, et toujours après coup. Dite une fois en tête, elle vaut pour
     tout ce que la section affiche, et les cartes peuvent disparaître.
     Cliquer ouvre la fenêtre : on lit l'état et on le change au même endroit. */
  const conn = (
    <button type="button" className="hr-tabaside-btn hr-tabaside-btn--icon prev-hyp"
            onClick={() => setShowAssum((v) => !v)}>
      <SlidersHorizontal size={15} />{p('Hypothèses')}
    </button>
  );

  /* Le compte de résultat, filtré par l'étape. Les SOUS-TOTAUX ne s'affichent
     que si l'étape les demande : une étape de saisie montre ses lignes, pas une
     cascade dont il manque des morceaux. */
  const pl = (sections, subs = null, ro = false, noCanPct = false, noFold = false) => (
    <ProfitLossPage embedded forecastMode readOnly={ro} noCanPct={noCanPct} noFold={noFold}
                    sectionFilter={sections} subtotalFilter={subs} unit={unit}
                    activeTenant={activeTenant} addToast={addToast} reloadKey={dataKey}
                    year={year} onYearChange={onYearChange} />
  );

  // Rendu de l'étape en cours. L'EXPORT, lui, sort le compte de résultat
  // entier : un classeur ne dépend pas de l'endroit où l'on s'est arrêté.
  const renderOne = (k) => {
    switch (k) {
      /* LES ACOMPTES SOUS LES REVENUS (2026-09-19) : ce qu'on a encaissé
         d'avance se reprend en chiffre d'affaires ; la carte se lit sous le
         revenu qu'elle alimente. */
      /* PAS DE « % CAN » SUR UNE ÉTAPE (2026-09-19) : la part du chiffre
         d'affaires se lit sur la CASCADE entière, où l'on compare des niveaux
         de marge. Sur les revenus, la table est elle-même ce chiffre
         d'affaires ; sur le personnel ou les autres charges, elle rapporte une
         poignée de lignes à un total qui n'est pas à l'écran. */
      case 'wc/advances': return <ForecastBalanceSheetTab focus="pos-advances" activeTenant={activeTenant} year={year} unit={unit} closeAction={closeAction} />;
      /* CRÉANCES CLIENTS EN UNE TABLE (demande d'Olivier, 2026-09-28) : le
         détail des Débiteurs se déplie sous « Nouvelles ventes » et sous leur
         encaissement, la carte du dessous est partie. */
      case 'wc/clients': return <ForecastBalanceSheetTab focus="pos-clients" activeTenant={activeTenant} year={year} unit={unit} closeAction={closeAction} />;

      /* LES COÛTS DIRECTS GARDENT LE REVENU SOUS LES YEUX (2026-09-18) : on
         planifie une charge CONTRE le chiffre d'affaires qui la porte, et un
         pourcentage se pose sur une base qu'il faut voir. */
      /* LES COÛTS DIRECTS SE LISENT EN DEUX BLOCS (demande d'Olivier,
         2026-09-26) : « Coûts des produits vendus » et « Autres coûts
         directs », comme au compte de résultat. La structure les sépare déjà,
         l'écran de saisie les alignait à plat. */
      case 'out/cogs':      return pl(['revenue', 'cogs'], ['cogs_goods', 'cogs_other']);
      case 'wc/suppliers': return <ForecastBalanceSheetTab focus="pos-fourn" activeTenant={activeTenant} year={year} unit={unit} closeAction={closeAction} />;
      case 'out/result':    return pl(null);
      /* PERSONNEL ET AUTRES CHARGES SANS CHEVRON (2026-10-03) : un seul bloc
         à l'écran, rien à replier. Le détail des charges sociales suit le
         personnel. */
      /* LES DEUX CARTES À LA MÊME LARGEUR (demande d'Olivier, 2026-10-03) :
         celle de la plus large, et elles rétrécissent ensemble avec l'écran. */
      case 'out/personnel': return (
        <div className="prev-stack">
          {pl(['personnel'], null, false, true, true)}
          <PayrollSocialCard activeTenant={activeTenant} year={year} unit={unit} reloadKey={dataKey} />
        </div>
      );
      case 'out/other':     return pl(['other_opex', 'non_operating'], null, false, true, true);
      case 'wc/vat':       return <VatYearPage activeTenant={activeTenant} year={year} unit={unit} closeAction={closeAction} />;
      case 'out/synthesis': return <PlanLandingSynthesis activeTenant={activeTenant} year={year} unit={unit} canWrite={scopes?.financials === 'rw'} addToast={addToast} />;
      /* REVENUS & MARGES = les Charges directes de Ventes & Ops, en vue pro
         (2026-09-29) : la même carte, le réalisé devant, une seule source. */
      case 'out/margins':   return <PurchasesMonthlyCard activeTenant={activeTenant} year={year} unit={unit} pro part="direct" canWrite={scopes?.financials === 'rw'} />;

      /* EMPRUNTS ET APPORTS : section de Finance depuis le 2026-09-27
         (`FinancingSection`), le plan de liquidité les lit. */
      case 'wc/socialflux': return <ForecastCashPlanTab activeTenant={activeTenant} year={year} unit={unit} pane="social" closeAction={closeAction} reloadKey={dataKey} />;
      case 'wc/start':    return <StartingPointPage activeTenant={activeTenant} year={year} unit={unit} closeAction={closeAction} onChanged={() => setDataKey((n) => n + 1)} />;
      case 'wc/moves':    return <CashMovesPage activeTenant={activeTenant} year={year} unit={unit} closeAction={closeAction} />;

      case 'synth/result':    return pl(null);
      case 'synth/balance':   return <ForecastBalanceSheetTab activeTenant={activeTenant} year={year} unit={unit} onAssumptionsChanged={reloadAssum} />;
      case 'synth/cashflow':  return <ForecastCashflowTab activeTenant={activeTenant} year={year} assumptions={assumptions} unit={unit} />;
      case 'wc/liquidity': return <ForecastCashPlanTab activeTenant={activeTenant} year={year} unit={unit} pane={planPane} onPane={setPlanPane} reloadKey={dataKey} />;
      default: return null;
    }
  };
  const renderStep = () => renderOne(here);
  const renderPl = () => (
    <ProfitLossPage embedded forecastMode sectionFilter={null} unit="chf"
                    activeTenant={activeTenant} addToast={addToast}
                    year={year} onYearChange={onYearChange} />
  );

  /* Échap ferme la fenêtre, comme partout ailleurs. */
  useEffect(() => {
    if (!showAssum) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowAssum(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAssum]);

  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);


  // Exports de la page → l'icône télécharger de la barre supérieure (registre
  // pageExports, 2026-08-18). PDF = rapport « Prévisionnel simplifié » du
  // registre d'impression ; Excel = « ce que tu vois » (tous les onglets, CHF).
  const excel = useHubExcelExport({
    tabs: [{ label: t('finance:forecast.tabs.pl'), render: () => renderPl() }],
    fileBase: t('phrases:p_0995a40774'),
    tenantName: activeTenant?.name,
    year,
    addToast,
  });
  const exportOptions = useMemo(() => [
    { id: 'excel', label: 'Excel', run: excel.run },
    /* Le Résultat prévisionnel et le Plan de liquidité ont leur PDF mis en page au serveur (la synthèse sur une page, puis le
       détail), comme le rapport financier ; les autres sections gardent le rapport imprimé (2026-10-22 et 23). */
    { id: 'pdf', label: 'PDF', run: async () => {
      const pdf = subject === 'out' ? ['forecast-pdf', 'Résultat prévisionnel'] : subject === 'wc' ? ['liquidity-pdf', 'Plan de liquidité'] : null;
      if (!pdf) { setShowPrint(true); return; }
      try {
        await downloadFile(`/financials/report/${tid}/${pdf[0]}?year=${year}`,
          `${activeTenant?.name || 'Rapport'} · ${year} · ${pdf[1]}.pdf`);
      } catch (e) { addToast?.(e.message, 'error'); }
    } },
  // eslint-disable-next-line
  ], [tid, year, subject]);
  useRegisterPageExports(exportOptions);

  /* Déclarées au gabarit, jamais posées à la main : la rangée garde ainsi la
     même place et le même costume que partout ailleurs. La période, elle,
     arrive du plan par le slot de la rangée, en vue graphique. */
  /* LA VUE DU PLAN NE DISPARAÎT PAS SOUS UN DÉTAIL (2026-09-25) : elle n'était
     déclarée que sur l'étape du plan, si bien qu'ouvrir un calcul emportait la
     bascule Table / Graphique et celle des flux. Elle reste donc sur tout le
     sujet, et la toucher REFERME le détail : elle pilote le plan, elle ne
     pourrait rien dire d'un écran de calcul. */
  /* DES VUES, PLUS DES ÉTAPES (demande d'Olivier, 2026-09-25) : on ne gravit
     pas les Opérations ni la Synthèse, on regarde tour à tour ce qu'elles
     montrent. Le rang numéroté promettait un parcours qui n'en est pas un.
     Elles prennent donc le RANG 1 du standard, la bascule de vue, au même
     format que « Vue : Table | Graphique » du plan. */
  const pageViews = subj.views ? [
    { id: 'view', kind: 'segmented', label: p('Vue'),
      value: step, onChange: setStep,
      options: subj.steps.map((st) => ({ id: st.id, label: st.label })) },
  ] : !subj.plain ? null : [
    { id: 'pane', kind: 'segmented', label: p('Vue'),
      value: planPane === 'chart' ? 'chart' : 'table',
      onChange: (v) => { setPlanPane(v === 'chart' ? 'chart' : 'plan'); setStep(plan.id); },
      options: [{ id: 'table', label: p('Table') }, { id: 'chart', label: p('Graphique') }] },
  ].filter(Boolean);

  return (
    /* Au GABARIT (2026-09-18) : le titre et la bande de contexte sur la même
       ligne, « Figer le budget » en action de page (c'est un geste, pas une
       vue), l'unité déclarée au registre comme partout, et les onglets
       dessous. Elle vivait sur la ligne des onglets avec le figeage. */
    <PageShell framed kind="report" unit={pageUnit} title={subj.label}
               views={pageViews}>
      {/* UNE SEULE PAGE, DES ÉTAPES (2026-09-18) : le hub portait quatre
          onglets qui filtraient tous LA MÊME table du compte de résultat, plus
          un cinquième qui la montrait entière. Deux chemins pour une même
          donnée, dont l'un disait qu'on planifie et l'autre qu'on lit, ne
          pouvaient pas cohabiter. Restent les étapes, dans l'ordre où l'on
          construit, sur une page comme la Synthèse financière.
          LES ÉTAPES À GAUCHE, LES COMMANDES À DROITE : on lit la progression
          dans le sens de la lecture, et ce qui règle la source se tient à
          l'autre bout, où l'on va le chercher sans le heurter. */}
      {/* LES RÉGLAGES DE SOURCE MONTENT SUR LA RANGÉE DES VUES (demande
          d'Olivier, 2026-09-26) : ils tenaient un étage à eux, sous elle, pour
          deux commandes alignées à droite. */}
      {connSlot && createPortal(conn, connSlot)}
      {plViewRow && <div className="prev-conn">{plViewRow}</div>}

      <div className="page-shell__panel">
        {renderStep()}
      </div>
      {assumPanel}

      {showPrint && (
        <PrintReportDocument
          activeTenant={activeTenant}
          year={year}
          reports={['forecast-standard']}
          mode="pdf"
          onClose={() => setShowPrint(false)}
        />
      )}
      {excel.stage}
    </PageShell>
  );
}
